import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useResolvedDisclosuresSendAction } from 'app/pages/loanApplication/hooks';
import { resolveMailDocumentPackageAction } from 'app/pages/loanApplication/LoanHeaders/RedesignedLoanInfoHeader/LoanActionsControls/LoanActionsControls.utils';

import { showMessageAction } from 'store/global/actions';
import {
  AttestationRecordOutcome,
  recordDisclosureAttestationsAction,
} from 'store/loanApplications/actions';
import { selectLoanActions } from 'store/loanApplications/selectors/loanActions.selectors';

import { useDisclosureReadiness, usePrepareDisclosuresWizard } from '../../../../../hooks';
import { WizardStep } from '../../../../../PrepareDisclosuresWizard.constants';
import { getBlockingCheckIds } from '../../../../../readiness/readinessChecks.utils';
import { summarizeDelivery } from '../../../BorrowersDeliveryStep/borrowersDelivery.utils';
import { MAIL_DISPATCH_COPY, SendStatus } from '../../sendOrchestration.constants';
import { areAttestationsComplete, buildAttestationPayload } from '../../sendOrchestration.utils';

import { executeMailDocumentPackage, executeSendFormBundles } from './utils';

const useDisclosureSend = attestations => {
  const dispatch = useDispatch();
  const {
    applicationId,
    loanId,
    previewResult,
    borrowersDelivery,
    isMailProviderEnabled,
    goToStep,
    setSent,
    setReadinessAlert,
  } = usePrepareDisclosuresWizard();
  const { checks, summary } = useDisclosureReadiness();
  const { sendAction, isLoading: isResolvingSendAction } = useResolvedDisclosuresSendAction(loanId);

  const loanActions = useSelector(selectLoanActions);
  const mailAction = useMemo(() => resolveMailDocumentPackageAction(loanActions), [loanActions]);
  const deliverySummary = useMemo(() => summarizeDelivery(borrowersDelivery), [borrowersDelivery]);

  const [status, setStatus] = useState(SendStatus.IDLE);
  const [errorMessage, setErrorMessage] = useState(null);

  const send = useCallback(async () => {
    setStatus(SendStatus.SENDING);
    setErrorMessage(null);

    if (!summary.isReadyToContinue) {
      setReadinessAlert(getBlockingCheckIds(checks));
      goToStep(WizardStep.READINESS);
      setStatus(SendStatus.BLOCKED_READINESS);

      return;
    }

    if (!areAttestationsComplete(attestations)) {
      setStatus(SendStatus.ERROR);

      return;
    }

    if (!sendAction) {
      setStatus(SendStatus.NO_SEND_ACTION);

      return;
    }

    const result = await dispatch(
      recordDisclosureAttestationsAction(
        buildAttestationPayload({ applicationId, previewResult, attestations }),
      ),
    );

    if (result?.outcome === AttestationRecordOutcome.UNAVAILABLE) {
      setStatus(SendStatus.UNAVAILABLE);

      return;
    }

    if (result?.outcome !== AttestationRecordOutcome.RECORDED) {
      setErrorMessage(result?.message ?? null);
      setStatus(SendStatus.ERROR);

      return;
    }

    try {
      await executeSendFormBundles(applicationId, sendAction);
    } catch {
      setStatus(SendStatus.ERROR);

      return;
    }

    // Best-effort, additive to the send that already succeeded above — its outcome is surfaced as
    // a toast, never as a send failure (the e-signed package already went out regardless).
    if (isMailProviderEnabled && deliverySummary.anyMail && !mailAction) {
      dispatch(showMessageAction({ message: MAIL_DISPATCH_COPY.notConfigured, variant: 'info' }));
    }

    if (isMailProviderEnabled && deliverySummary.anyMail && mailAction) {
      try {
        await executeMailDocumentPackage(applicationId, mailAction);
        dispatch(showMessageAction({ message: MAIL_DISPATCH_COPY.dispatched, variant: 'success' }));
      } catch {
        dispatch(showMessageAction({ message: MAIL_DISPATCH_COPY.failed, variant: 'error' }));
      }
    }

    setSent(true);
    setStatus(SendStatus.SENT);
  }, [
    applicationId,
    previewResult,
    attestations,
    summary,
    checks,
    sendAction,
    dispatch,
    goToStep,
    setSent,
    setReadinessAlert,
    isMailProviderEnabled,
    deliverySummary,
    mailAction,
  ]);

  return {
    status,
    send,
    errorMessage,
    sendAction,
    isResolvingSendAction,
  };
};

export default useDisclosureSend;
